import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  ArrowsClockwise, Wallet, ShieldCheck, Globe, DeviceMobile, Desktop, ArrowSquareOut, Lightning, Fire
} from "@phosphor-icons/react";

const TYPE_STYLES = {
  "Hot Wallet": { bg: "bg-amber-900/20", text: "text-amber-400", border: "border-amber-800/30" },
  "Hardware Wallet": { bg: "bg-blue-900/20", text: "text-blue-400", border: "border-blue-800/30" },
  "Hardware + Hot Wallet": { bg: "bg-violet-900/20", text: "text-violet-400", border: "border-violet-800/30" },
};

const CHAIN_COLORS = {
  "Ethereum": "#627EEA", "BSC": "#F0B90B", "Solana": "#14F195", "Polygon": "#8247E5",
  "Arbitrum": "#28A0F0", "Optimism": "#FF0420", "Avalanche": "#E84142", "Base": "#0052FF",
  "Bitcoin": "#F7931A", "Tron": "#FF0013", "Cosmos Hub": "#2E3148", "PulseChain": "#00FF00",
  "zkSync Era": "#4E529A", "Linea": "#61DFFF", "Blast": "#FCFC03", "Fantom": "#1969FF",
  "Sui": "#4DA2FF", "Aptos": "#2ED8A3", "TON": "#0088CC", "Cardano": "#0033AD",
  "Polkadot": "#E6007A", "Cosmos": "#2E3148", "Mantle": "#000000", "Scroll": "#FFEEDA",
};

function WalletRow({ wallet }) {
  const style = TYPE_STYLES[wallet.type] || TYPE_STYLES["Hot Wallet"];
  const rankColors = ["text-amber-400", "text-zinc-300", "text-amber-700"];

  return (
    <div
      data-testid={`wallet-row-${wallet.rank}`}
      className="border-b border-zinc-800/40 hover:bg-zinc-900/40 transition-colors px-4 py-4"
    >
      {/* Top row: rank, name, type, link */}
      <div className="flex items-center gap-3 mb-2.5">
        <span className={`text-sm font-data font-bold w-7 text-center flex-shrink-0 ${
          wallet.rank <= 3 ? rankColors[wallet.rank - 1] : "text-zinc-600"
        }`}>
          {wallet.rank}
        </span>

        {wallet.logo ? (
          <img
            src={wallet.logo}
            alt={wallet.name}
            className="w-8 h-8 rounded-lg flex-shrink-0 bg-zinc-800 object-contain p-0.5"
            loading="lazy"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
            <Wallet size={16} className="text-zinc-500" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-zinc-100">{wallet.name}</span>
            <Badge className={`text-[7px] px-1.5 py-0 border-0 rounded-sm h-3.5 ${style.bg} ${style.text}`}>
              {wallet.type.toUpperCase()}
            </Badge>
            {wallet.type === "Hardware Wallet" && (
              <ShieldCheck size={12} className="text-blue-400" weight="fill" />
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-[10px] text-zinc-500">{wallet.category}</span>
            <span className="text-[10px] text-zinc-600">Est. {wallet.year}</span>
            <span className="text-[10px] text-zinc-500 flex items-center gap-0.5">
              <Fire size={9} className="text-amber-500" />
              {wallet.users}
            </span>
          </div>
        </div>

        <a
          href={wallet.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`wallet-link-${wallet.rank}`}
          className="flex items-center gap-1 text-[10px] text-zinc-500 hover:text-blue-400 transition-colors px-2 py-1 border border-zinc-800 rounded-sm flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <ArrowSquareOut size={10} />
          Visit
        </a>
      </div>

      {/* Supported Chains */}
      <div className="ml-10 pl-0.5">
        <div className="flex items-center gap-1.5 mb-1.5">
          <Globe size={10} className="text-zinc-600" />
          <span className="text-[9px] uppercase tracking-wider text-zinc-600 font-medium">
            {wallet.chain_count} Blockchain{wallet.chain_count !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {wallet.chains.map((chain) => {
            const color = CHAIN_COLORS[chain] || "#71717a";
            return (
              <span
                key={chain}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] text-zinc-300 border border-zinc-800/60 bg-zinc-900/40"
              >
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                {chain}
              </span>
            );
          })}
        </div>
      </div>

      {/* Features + Platforms */}
      <div className="ml-10 pl-0.5 mt-2 flex items-start gap-6">
        <div>
          <span className="text-[9px] uppercase tracking-wider text-zinc-600 font-medium flex items-center gap-1 mb-1">
            <Lightning size={9} /> Features
          </span>
          <div className="flex flex-wrap gap-1">
            {wallet.features.map((f) => (
              <span key={f} className="text-[9px] text-zinc-500 px-1.5 py-0.5 bg-zinc-900/50 rounded-sm border border-zinc-800/40">
                {f}
              </span>
            ))}
          </div>
        </div>
        <div className="flex-shrink-0">
          <span className="text-[9px] uppercase tracking-wider text-zinc-600 font-medium flex items-center gap-1 mb-1">
            <DeviceMobile size={9} /> Platforms
          </span>
          <div className="flex flex-wrap gap-1">
            {wallet.platforms.map((p) => (
              <span key={p} className="text-[9px] text-zinc-400 px-1.5 py-0.5 bg-zinc-800/50 rounded-sm">
                {p}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CryptoWallets() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all");

  const fetchWallets = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const resp = await axios.get(`${API}/wallets`);
      setData(resp.data);
    } catch (e) {
      console.error("Failed to fetch wallets:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchWallets(); }, []);

  const wallets = data?.wallets || [];
  const filtered = filter === "all" ? wallets
    : filter === "hardware" ? wallets.filter(w => w.type.toLowerCase().includes("hardware"))
    : wallets.filter(w => w.type === "Hot Wallet");

  const hardwareCount = wallets.filter(w => w.type.toLowerCase().includes("hardware")).length;
  const hotCount = wallets.filter(w => w.type === "Hot Wallet").length;

  return (
    <div className="p-5 max-w-[1200px]" data-testid="wallets-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <Wallet size={22} weight="bold" className="text-cyan-400" />
            <h1 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">Crypto Wallets</h1>
          </div>
          <p className="text-xs text-zinc-500 ml-[34px]">
            Top 20 popular wallets ranked by adoption, with blockchain compatibility
          </p>
        </div>
        <button
          data-testid="refresh-wallets-btn"
          onClick={() => fetchWallets(true)}
          disabled={refreshing}
          className="flex items-center gap-1.5 text-[10px] text-zinc-400 hover:text-zinc-200 transition-colors px-2.5 py-1.5 border border-zinc-800 rounded-sm"
        >
          <ArrowsClockwise size={12} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Updating..." : "Refresh"}
        </button>
      </div>

      {/* Summary Cards */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="wallets-summary">
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3.5">
            <div className="flex items-center gap-2 mb-1.5">
              <Wallet size={14} className="text-cyan-400" />
              <span className="text-[9px] uppercase tracking-wider text-zinc-500">Total Wallets</span>
            </div>
            <p className="text-lg font-data font-bold text-zinc-100">{wallets.length}</p>
          </div>
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3.5">
            <div className="flex items-center gap-2 mb-1.5">
              <Lightning size={14} className="text-amber-400" />
              <span className="text-[9px] uppercase tracking-wider text-zinc-500">Hot Wallets</span>
            </div>
            <p className="text-lg font-data font-bold text-zinc-100">{hotCount}</p>
          </div>
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3.5">
            <div className="flex items-center gap-2 mb-1.5">
              <ShieldCheck size={14} className="text-blue-400" />
              <span className="text-[9px] uppercase tracking-wider text-zinc-500">Hardware Wallets</span>
            </div>
            <p className="text-lg font-data font-bold text-zinc-100">{hardwareCount}</p>
          </div>
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3.5">
            <div className="flex items-center gap-2 mb-1.5">
              <Globe size={14} className="text-emerald-400" />
              <span className="text-[9px] uppercase tracking-wider text-zinc-500">Max Chain Support</span>
            </div>
            <p className="text-lg font-data font-bold text-zinc-100">
              {Math.max(...wallets.map(w => w.chain_count))} chains
            </p>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 mb-4" data-testid="wallet-filters">
        {[
          { key: "all", label: "All Wallets" },
          { key: "hot", label: "Hot Wallets" },
          { key: "hardware", label: "Hardware" },
        ].map(({ key, label }) => (
          <button
            key={key}
            data-testid={`filter-${key}`}
            onClick={() => setFilter(key)}
            className={`text-[10px] px-3 py-1.5 rounded-sm border transition-colors ${
              filter === key
                ? "bg-zinc-800 text-zinc-100 border-zinc-600"
                : "bg-zinc-900/30 text-zinc-500 border-zinc-800/60 hover:text-zinc-300 hover:border-zinc-700"
            }`}
          >
            {label} ({key === "all" ? wallets.length : key === "hardware" ? hardwareCount : hotCount})
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading && (
        <div className="space-y-3">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-28 bg-zinc-900/50" />)}
        </div>
      )}

      {/* Wallet List */}
      {!loading && filtered.length > 0 && (
        <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-sm overflow-hidden" data-testid="wallets-table">
          {filtered.map((w) => (
            <WalletRow key={w.rank} wallet={w} />
          ))}
        </div>
      )}

      {/* Footer */}
      {data && (
        <div className="mt-4 pt-3 border-t border-zinc-800/40 flex items-center justify-between">
          <p className="text-[10px] text-zinc-600">Curated wallet data. Always verify compatibility before use.</p>
          <p className="text-[10px] text-zinc-600">
            Updated: {new Date(data.fetched_at).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
}
